import { useEffect, useRef, useMemo, useState, ReactNode } from "react";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import { animated, config, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import useClickOutside from "hooks/useClickOutside";
import { useDialogWindowStore } from "stores/dialogWindow";
import Portal from "../Portal";
import PageTitle from "../PageTitle";

import "./styles.sass";

type BottomSheetProps = {
  children: JSX.Element;
  height?: "max" | "auto" | number;
  variation?: "primary" | "banger-gradient";
  title: string | ReactNode;
  onClose?: () => void;
  className?: string;
  lock?: boolean;
};
const BottomSheet = (props: BottomSheetProps) => {
  const {
    children,
    height = "max",
    variation = "primary",
    title,
    onClose,
    className,
    lock = false,
  } = props;
  const location = useLocation();
  const [childHeight, setChildHeight] = useState<number>(() => {
    return typeof height === "number" ? height : 0;
  });

  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const isDialogWindowOpen = useDialogWindowStore(
    (state) => state.isDialogWindowOpen,
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    openSheet({ canceled: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const heightContent = useMemo(() => {
    if (height === "max") {
      return window.innerHeight - 85;
    }
    return childHeight + 76;
  }, [height, childHeight]);

  const [{ y }, api] = useSpring(() => ({
    y: heightContent,
  }));

  const navigate = useNavigate();

  const contentRef = useRef(null);
  const childrenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (height === "auto" && children) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const child = childrenRef.current.childNodes;
      // eslint-disable-next-line @typescript-eslint/no-shadow
      let childHeight = 0;
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < child.length; i++) {
        childHeight += (child[i] as any).clientHeight;
      }
      setChildHeight(childHeight);
    }
  }, [height, children]);

  const openSheet = ({ canceled }) => {
    api.start({
      y: 0,
      immediate: false,
      config: canceled ? config.default : config.stiff,
    });
  };

  const closeSheet = (velocity: number = 0) => {
    if (!lock) {
      api.start({
        y: heightContent,
        immediate: false,
        config: { ...config.stiff, velocity },
      });
      setTimeout(() => {
        if (onClose) {
          onClose();
        } else {
          navigate(location.pathname);
        }
      }, 500);
    }
  };

  useEffect(() => {
    const handleScroll = (e) => {
      setScrollPosition(e.target.scrollTop);
    };
    if (childrenRef.current) {
      childrenRef.current.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (childrenRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        childrenRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const handleTouchMove = (e) => {
    if (!e.touches || e.touches.length < 1 || !e.touches[0].clientY) {
      return;
    }

    const currentTouchY = e.touches[0].clientY;

    if (
      childrenRef.current &&
      scrollPosition ===
        childrenRef.current.scrollHeight - childrenRef.current.clientHeight &&
      currentTouchY < e.touches[0].clientY
    ) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (childrenRef.current) {
      childrenRef.current.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
    }

    return () => {
      if (childrenRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        childrenRef.current.removeEventListener("touchmove", handleTouchMove);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition]);

  const bind = useDrag(
    ({
      event,
      last,
      velocity: [, vy],
      direction: [, dy],
      offset: [, oy],
      cancel,
      canceled,
    }) => {
      if (
        (childrenRef.current &&
          childrenRef.current.contains(event.target as Node)) ||
        lock
      ) {
        cancel();
        return;
      }

      if (oy < -70) {
        cancel();
      }

      if (last) {
        // eslint-disable-next-line no-unused-expressions
        oy > heightContent * 0.5 || (vy > 0.5 && dy > 0)
          ? closeSheet(vy)
          : openSheet({ canceled });
      } else {
        api.start({ y: oy, immediate: true });
      }
    },
    {
      from: () => [0, y.get()],
      filterTaps: true,
      bounds: { top: 0 },
      rubberband: true,
    },
  );

  const BottomSheetClass = classNames("BottomSheet__content", className, {
    [`BottomSheet__content_${variation}`]: variation,
  });

  useClickOutside({
    refs: [contentRef],
    handler: () => {
      if (!isDialogWindowOpen) {
        closeSheet(0);
      }
    },
  });

  const isSamsungBrowser = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.indexOf("samsungbrowser") !== -1;
  };

  return (
    <Portal>
      <div className="BottomSheet">
        <animated.div
          className={BottomSheetClass}
          ref={contentRef}
          {...bind()}
          style={{
            height: `${isSamsungBrowser() ? window.innerHeight - 85 : heightContent}px`,
            bottom: "0px",
            y,
          }}
        >
          {!lock && <div className="BottomSheet__swipe" />}
          <div className="BottomSheet__header">
            <PageTitle
              title={title}
              // eslint-disable-next-line react/jsx-handler-names
              onClose={lock ? null : () => closeSheet(0)}
            />
          </div>
          <div ref={childrenRef} className="BottomSheet__children">
            {children}
          </div>
        </animated.div>
      </div>
    </Portal>
  );
};

export default BottomSheet;
